import { ClaraSceneService, ClaraSceneUtilities, eHighlightType, SceneCache } from "@app/helpers/clara-scene.service";
import { Configurator, eDragMode, eHotspotShape, eRuleType, Field, Hotspot, IAddModelClickHandlerArgs, IClaraSceneRuleArgs, IClickArgs, IConstrainCameraArgs, IDietCompany, IDragArgs, IDraggable, IGetTableRuleArgs, IGetUploadImageNodeArgs, IHandler, IHandlerDb, IJsResult, ISceneAnimationArgs, ISceneApi, ISceneRuleArgs, ISceneSetPropertyArgs, IToggleToolArgs, KbObjectManager, UiObject } from "@models";
import { ClientLogger, KPromise } from "@rules";
import { IMessage, Utils } from "@tools";
import { ILoadSceneArgs, ISceneViewerArgs, SceneViewer } from "./scene-viewer";

export interface IClaraSceneViewerArgs extends ISceneViewerArgs{
    sceneService: ClaraSceneService;
    playerUrl: string;
    ocLazyLoad: oc.ILazyLoad;
}

export class HandlerDb<T extends IHandler> implements IHandlerDb<T>{
    constructor(public sceneApi: ISceneApi, public sceneService: ClaraSceneService) {

    }

    // public byHandler: { [handlerId: string]: T } = {};
    public byNode: { [nodeId: string]: T[] } = {};

    public clear() {
        // this.byHandler = {};
        this.byNode = {};
    }

    public clearTransient() {
        for (let key in this.byNode) {
            let h = this.byNode[key];
            if (h) {
                let removed = h.removeWhere(h1 => h1.addedByRuleType && h1.addedByRuleType.equalsAny(eRuleType.value, eRuleType.scene));
                if (removed && removed.length && !h.length) {
                    delete this.byNode[key];
                }
            }
        }
    }

    protected addToDb(id: string, handler: T) {
        if (!this.byNode.hasOwnProperty(id)) {
            this.byNode[id] = [];
        }
        this.byNode[id].push(handler);
    }

    public add(node: string, handlerId: string, handler: T) {
        let hotspotId = (handler as any).hotspotId;
        if (hotspotId) {
            //for non-mesh hotspots, we just add the hotspot id to the clickdb
            this.addToDb(hotspotId, handler);
        } else {
            let query = this.sceneService.getQueryObject(this.sceneApi, node, null);
            let matchingNodes = this.sceneApi.cache.paths.getIds(query);

            matchingNodes.forEach(matchingNode => {
                this.addToDb(matchingNode, handler);
            });
        }

        //add the node id to map to itself
        // db.nodeMap[args.modelId] = args.modelId;
        //add children of the node to the map
        // this.sceneService.getChildNodeIds(this.sceneApi, args.modelId).forEach(i => db.nodeMap[i] = args.modelId);
        // }
    }

}

export class ClaraSceneRuleArgs implements IClaraSceneRuleArgs {
    constructor(public viewer: ClaraViewer) {
        this.isMobile = viewer.isMobile;
    }
    public environment: string;
    public company: IDietCompany;
    public configurator: Configurator;
    public kom: KbObjectManager;
    public parentKom: KbObjectManager;
    public sceneKom: KbObjectManager;
    public sceneApi: ISceneApi;
    public isRender: boolean;
    public isMobile: boolean;
    public clientLanguage: string;
    public sceneUtils: ClaraSceneUtilities;
    public logs: ClientLogger = new ClientLogger();
    // public upload() {
    //     return this.viewer.upload();
    // }

    // TODO:  remove in future.  It's only here for backwards compatibility.  It's true place now is in SceneUtilities
    public addModelClickHandler(args: IAddModelClickHandlerArgs) {
        this.sceneUtils.addModelClickHandler(args);
    }
    public addDraggable(args: IDraggable) {
        this.sceneUtils.addDraggable(args);
    }
    public playSceneAnimation(args) {
        // DEPRECATED
    }
    public toggleSceneAnimation() {
        // DEPRECATED
    }
    public sendMessage(msg: IMessage) {
        this.viewer.sendMessage(msg);
    }
    public selectPage(page) {
        if (this.viewer.navigateToElement) {
            this.viewer.navigateToElement(page);
        }
    }
    public convertCurrency(obj) {
        return null;
    }
    public toggleTool(args: IToggleToolArgs) {
        this.viewer.sceneService.toggleTool(this.sceneApi, args);
        if (args.tool == "orbit") {
            this.viewer.sceneConfig.allowOrbit = args.enabled;
        }
        else if (args.tool == "pan") {
            this.viewer.sceneConfig.allowPan = args.enabled;
        }
        else if (args.tool == "zoom") {
            this.viewer.sceneConfig.allowZoom = args.enabled;
        }
    }
    public constrainCamera(args: IConstrainCameraArgs) {
        this.viewer.sceneService.constrainCamera(this.sceneApi, args);
    }
    public getUploadImageNode(args: IGetUploadImageNodeArgs) {
        let a = this.sceneApi.importedImages[this.configurator.name + "_" + args.fieldId];
        return a ? a.imageNodeId : null;
    }
    public getTables(args: IGetTableRuleArgs) {
        return new KPromise(args.ids.map(tid => this.viewer.tableArraysDb[tid]));
    }
}


export class ClaraViewer extends SceneViewer {
    constructor(cArgs: IClaraSceneViewerArgs) {
        super(cArgs);
        this.sceneService = cArgs.sceneService;
        this.playerUrl = cArgs.playerUrl;
        this.ocLazyLoad = cArgs.ocLazyLoad;

        this.sceneApi = {
            isMobile: cArgs.isMobile,
            isRender: this.isRender,
            clientLanguage: cArgs.clientLanguage,
            sceneDb: cArgs.sceneDb,
            onSceneNested: (nested: Configurator, nodeId: string) => this.onSceneNested(nested, nodeId),
            afterSetProperty: setArgs => this.afterSetProperty(setArgs),
            afterAnimateProperty: setArgs => this.afterAnimateProperty(setArgs),
            addModelClickHandler: args => this.sceneApi.clickDb.add(args.modelId, args.uniqueId, args),
            clearClickHandlers: () => this.sceneApi.clickDb = new HandlerDb(this.sceneApi, this.sceneService),
            addDraggable: args => this.sceneApi.draggableDb.add(args.objectId, args.uniqueId, args),
            clearDraggables: () => this.sceneApi.draggableDb = new HandlerDb(this.sceneApi, this.sceneService),
            sendMessage: (msg) => this.sendMessage(msg),
            fetchedScenes: {},
            importedImages: {},
            getTables: args => new KPromise(args.ids.map(tid => this.tableArraysDb[tid])),
            cameraAnimationInProgress: false,
            // cameraAnimationPromise: new KPromise(null)
            highlights: { user: {}, hover: {}, selection: {} },
            selectedNodes: []
        };
        this.sceneApi.clickDb = new HandlerDb(this.sceneApi, this.sceneService);
        this.sceneApi.draggableDb = new HandlerDb(this.sceneApi, this.sceneService);
        this.sceneApi.cache = new SceneCache(this.sceneApi);
        //this.$scope = this.controllerScope.$new();
        this.$scope.hotspotClick = ($event, ann) => this.hotspotClick($event, ann);
        this.$scope.hotspotCloseClick = ($event, ann) => this.hotspotCloseClick($event, ann);
        // this.$scope.vrMode = false;
        // this.$scope.toggleVrMode = () => this.toggleVrMode();
        // this.$scope.arMode = false;
        // this.$scope.toggleArMode = () => this.toggleArMode();
        Utils.supportsVr().then(res => this.$scope.supportsVr = res);
        this.$scope.allHotspots = [];
    }


    public sceneService: ClaraSceneService;
    public playerUrl: string;
    public ocLazyLoad: oc.ILazyLoad;
    public sceneApi: ISceneApi;    
    private hovers: { nodeId: string /*, origMatId: string; hoverMatId: string */ }[] = [];

    /**
     * in the form of {annotationId: hotspotId}
     */
    private annotationToHotspotDb: {} = {};
    /**
     * stores what scene Id's we've already prefetched so we don't bother prefetching them again.  
     * Once they are prefetched they are in the browser cache.
     */
    public prefetchedScenes: { [claraId: string]: any } = {};
    /**We have mousedown and click both looking for the same filternodesfromposition result.  So we store it on one event loop to not call it multiple times */
    public currentFilterNodes: string[];

    public static detectWebGL(): boolean {
        try {
            let canvas = document.createElement("canvas");
            return !!(
                (window as any).WebGLRenderingContext &&
                (canvas.getContext("webgl") || canvas.getContext("experimental-webgl")));
        } catch (e) {
            return false;
        }
    }

    

    public init() {
        return this.ocLazyLoad.load(this.playerUrl).then(() => {
            // initialize clara viewer
            this.createElem();        
            let api = claraplayer(this.elem);
            this.sceneApi.api = api;
            ["orbit", "pan", "zoom", "home", "fullscreen", "vrSettings", "arMode"].forEach(s =>
                api.player.hideTool(s)); // hide the icons         
            api.player.removeTool("select");
            // turn off the thumbnail
            api.player.displayThumbnail(false);
            // setup highlighting
            this.sceneApi.highlighter = api.selection.setHighlighting(true, { color: this.highlightColor, thickness: 2 });

            /* we use both the customAnnotationFn (for when a hotspot is tied to an annotation) and 
            useCustomOverlay for hotspots that are tied to other types of nodes.
            We can't use useCustomOverlay for both because annotations aren't loaded into the scene 
            and getScenePosition(idAnnotation) won't work as a result */
            api.annotations.useCustomAnnotationFunction((a, d) => this.annotationFn(a, d));
            api.annotations.useCustomOverlayFunction(d => this.customOverlayFn(d));

            this.createHotspotElem();

            // add click event
            api.player.addTool({
                click: event => this.click(event)
            }, "ComponentClick");

            // add hover event
            api.player.addTool({
                hover: event => this.hover(event)
            }, "Hover");

            // add hover event
            api.player.addTool({
                mousedown: event => this.mousedown(event)
            }, "Mousedown");
        });        
    }

    public loadSceneInternal(loadSceneArgs: ILoadSceneArgs): ng.IPromise<void> {
        // flush out stuff that needs to be flushed
        this.clearHandlers();
        this.annotationToHotspotDb = {};
        this.sceneApi.cache = new SceneCache(this.sceneApi);
        this.sceneApi.highlights = { user: {}, hover: {}, selection: {} };
        this.sceneApi.selectedNodes = [];
                
        let api = this.sceneApi.api;
        if (loadSceneArgs.clearMemory) {
            this.sceneApi.api.sceneIO.clearScene();
            this.sceneApi.fetchedScenes = {}; // clear out the fetched scenes since they were just cleared out of memory
            this.sceneApi.importedImages = {};
        }

        // toggle tools based on initial scene configurator properties
        this.toggleTools();
        
        if (this.sceneConfig.constrainCameraToRadius) {
            this.sceneService.constrainCamera(
                this.sceneApi,
                {
                    constrain: this.sceneConfig.constrainCameraToRadius,
                    radius: this.sceneConfig.cameraRadius
                });
        }

        // 'rendered' event is when the loading animation is done - we might want to use this when clearing memory
        // new loading animation too
        // turn thumbnail off with displayThumbnail in player
        let rulesRun = false; // to get around a bug in clara where resize causes the loaded event to fire
        api.on("loaded", () => {
            if (!rulesRun) {
                rulesRun = true;
                this.sceneApi.hierarchicalVisibility = this.sceneService.isHierarchicalVisibilityEnabled(this.sceneApi);
                this.shimThree(this.sceneApi);

                this.runLoadedRule(this.getRuleArgs(eRuleType.sceneLoaded)).then(() => {
                    if (this.sceneApi.api.commands.isCommandEnabled) {
                        // need to check armode here because it's calculation is delayed
                        this.$scope.arEnabled = this.sceneApi.api.commands.isCommandEnabled("arMode");
                    }
                    
                    this.$timeout(() => this.resize(), 0);
                    // prefetch all scenes so that nested scenes and d&d components load faster
                    this.prefetchScenes(this.sceneApi);
                });
            }
        });

        this.sceneApi.fetchedScenes[this.sceneConfig.claraId] = api.sceneIO.fetchAndUse(
            this.sceneConfig.claraId,
            this.scene.publishHash,
            { waitForPublish: true }
        );
        this.prefetchedScenes[this.sceneConfig.claraId] = null;

        return this.loadDeferred.promise;
    }

    protected toggleTools()
    {
        // toggle tools based on initial scene configurator properties
        this.sceneService.toggleTool(this.sceneApi, { tool: "orbit", enabled: this.sceneConfig.allowOrbit });
        this.sceneService.toggleTool(this.sceneApi, { tool: "pan", enabled: this.sceneConfig.allowPan });
        this.sceneService.toggleTool(this.sceneApi, { tool: "zoom", enabled: this.sceneConfig.allowZoom });
        if (this.sceneConfig.allowOrbit)
            this.sceneApi.api.commands.activateCommand("orbit");
        else if (this.sceneConfig.allowPan)
            this.sceneApi.api.commands.activateCommand("pan");
        else if (this.sceneConfig.allowZoom)
            this.sceneApi.api.commands.activateCommand("zoom");
    }

    protected runLoadedRule(ruleArgs: ISceneRuleArgs) {
        // run the loaded rule of the scene
        this.setFieldsIfStandalone();

        return this.ruleService.runRuleTypeAsync(this.sceneConfig, eRuleType.sceneLoaded, ruleArgs).then(loadedRuleResult => {
            this.handleRuleError(eRuleType.sceneLoaded, loadedRuleResult);
            return this.runRules(false).then(() => {
               this.notifyLoaded();
            });
        });
    }
    // protected getDragOffset(ev: clara2.IMouseEvent, d: IDraggable): THREE.Vector3 {
    //     let three = this.sceneService.getThree(this.sceneApi);
    //     let referPoint = new three.Vector3();
    //     let offset = new three.Vector3(0, 0, 0);
    //     let ray: THREE.Ray = void 0;

    //     if (d.mode == eDragMode.plane) {
    //         if (ev.eventRay) {
    //             ray = ev.eventRay.ray;
    //         } /*else {
    //         let rect = this.elem.getBoundingClientRect();
    //         let ndc = { //normalized device coordinates.  x & y should be between -1 and 1
    //             x: (2 * ev.clientX - rect.width) / rect.width,
    //             y: (rect.height - 2 * ev.clientY) / rect.height
    //         };
    //         ray = store.getTranslator().getCameraMouseRay(ndc).ray;
    //     }*/
    //         if (d.mode == eDragMode.plane) {
    //             let moveOnPlane = new three.Plane();
    //             let moveOnNormal = new three.Vector3();
    //             moveOnNormal.set(d.normal.x, d.normal.y, d.normal.z);
    //             moveOnPlane.set(moveOnNormal, -(d.distanceFromOrigin || 0)); //plane.constant);
    //             if (!ray.intersectsPlane(moveOnPlane)) return;

    //             ray.intersectPlane(moveOnPlane, referPoint);
    //         }

    //         var nodeMatrix = this.sceneApi.api.scene.getWorldTransform(d.$activeNode.id);
    //         var nodePosition = new three.Vector3().setFromMatrixPosition(nodeMatrix);
    //         offset = referPoint.sub(nodePosition);
    //     }
    //     return offset;
    // }

    // /** draggables store the nodes they match, but since they support regex too, at some point we need to run those searches to find the matching nodes to compare against for mouse events */
    // protected fillDraggables() {
    //     let sceneApi = this.sceneApi;
    //     for (let d of sceneApi.draggables) {
    //         //get all of the matching node id's to the query provided, and cache for fast retrieval
    //         if (!d.$matchingNodes) {
    //             d.$matchingNodes = {};
    //             let query = this.sceneService.getQueryObject(sceneApi, d.objectId, d.name);
    //             sceneApi.cache.paths.getIds(query).forEach(id => d.$matchingNodes[id] = {});
    //             //also get all of the children of the matching nodes
    //             // for (let mid in d.$matchingNodes) {
    //             //     sceneApi.cache.paths.getIds({ from: mid }).forEach(id => d.$matchingNodes[mid][id] = null);
    //             // }
    //         }
    //     }
    // }

    private _clickAlreadyHandled = false; //during drags, the mousedown might initiate the click event.  We mark whether it has, so the click handler can ignore.
    public mousedown(event: clara2.IMouseEvent) {
        let sceneApi = this.sceneApi;
        let api = sceneApi.api;
        this._clickAlreadyHandled = false;

        //search for draggables
        if (Object.keys(sceneApi.draggableDb.byNode).length) {
            // this.fillDraggables();

            let hits = this.currentFilterNodes = api.player.filterNodesFromPosition(event);
            let hit = hits.first();

            let activeDrag: IDraggable = null;
            let activeNodeId = this.sceneService.findAncestorNodeId(
                this.sceneApi,
                hit,
                nid => {
                    activeDrag = sceneApi.draggableDb.byNode[nid] ? sceneApi.draggableDb.byNode[nid].first() : null;
                    return activeDrag != null;
                }
            );

            if (activeDrag) {
                //ok we have an active drag, so we select the node
                this.sceneService.select(sceneApi, { id: activeNodeId });
                let dragStarted = false;
                let sceneUtils = this.sceneService.getSceneUtilities(sceneApi, eRuleType.scene, {}, null);
                let dragNode = activeDrag.$activeNode = sceneUtils.getNode({ id: activeNodeId });
                let three = this.sceneService.getThree(sceneApi);
                let startPos = (new three.Vector3()).setFromMatrixPosition(api.scene.getWorldTransform(activeNodeId));
                // let offset = this.getDragOffset(event, activeDrag);

                //console.log({ event: "beforeDrag", x: startPos.x, y: startPos.y, z: startPos.z });
                //and we start the nodeMove command so they can drag it
                api.commands.setCommandOptions('nodeMove', {
                    displayGizmo: false,
                    mode: activeDrag.mode,
                    plane: activeDrag.mode == eDragMode.plane ? { normal: activeDrag.normal, constant: activeDrag.distanceFromOrigin ? -activeDrag.distanceFromOrigin : 0 } : null,
                    surfaceNodeIds: activeDrag.mode == eDragMode.surface ? (Array.isArray(activeDrag.surfaceNodes) ? activeDrag.surfaceNodes : [activeDrag.surfaceNodes]) : null,
                    keepOffset: true,
                    shouldMove: (event, newPos, data) => {
                        // offset && newPos.sub(offset); // temp fix for clara bug:  https://github.com/Exocortex/ExocortexKBMax/issues/208
                        var hitData = data ? data.first() : null;
                        var uv = hitData ? hitData.uv : null;
                        let args = { dragNode, startPos, newPos, allow: true, relativeSurfacePos: uv } as IDragArgs;
                        if (!dragStarted) {
                            dragStarted = true;
                            activeDrag.dragStart && activeDrag.dragStart(args);
                            //console.log({ event: "dragStart", x: args.newPos.x, y: args.newPos.y, z: args.newPos.z });
                        }
                        if (activeDrag.dragging && dragStarted) {
                            activeDrag.dragging(args);
                            //console.log({ event: "dragging", x: args.newPos.x, y: args.newPos.y, z: args.newPos.z });
                        }
                        // offset && newPos.add(offset); // temp fix for clara bug:  https://github.com/Exocortex/ExocortexKBMax/issues/208
                        return args.allow;
                    },
                    // updatePosition: (newPos, surface) => {
                    //     newPos.sub(offset); // temp fix for clara bug:  https://github.com/Exocortex/ExocortexKBMax/issues/208
                    //     let args = { dragNode, startPos, newPos } as IDragArgs;
                    //     if (activeDrag.dragging) {
                    //         activeDrag.dragging(args);
                    //     }
                    //     newPos.add(offset); // temp fix for clara bug:  https://github.com/Exocortex/ExocortexKBMax/issues/208
                    // },
                    onEnd: (event) => {
                        this.$timeout(() => {
                            //if the drag never started, then we don't have a drop event
                            if (dragStarted) {
                                let args = { dragNode, allow: true, startPos, newPos: (new three.Vector3()).setFromMatrixPosition(api.scene.getWorldTransform(activeNodeId)), relativeSurfacePos: null } as IDragArgs;
                                activeDrag.dropped && activeDrag.dropped(args);
                                //console.log({ event: "dropped", x: args.newPos.x, y: args.newPos.y, z: args.newPos.z });
                            }
                            this.toggleTools();
                            sceneApi.activeDrag = null;
                            this.click(event);
                        });
                    }
                } as clara2.INodeMoveOptions);
                api.commands.runCommand("nodeMove");
            } else {
                this.sceneService.clearSelection(sceneApi);
                this.toggleTools();
                sceneApi.activeDrag = null;
            }
        }
    }

    public click(event: clara2.IMouseEvent) {
        if (!this._clickAlreadyHandled) {
            this._clickAlreadyHandled = true;
            this.$timeout(() => {
                this.clearAnnotations();
                let db = this.sceneApi.clickDb;
                //let idsToFilterBy = Object.keys(db.nodeMap);
                let nodeIds = this.currentFilterNodes || this.sceneApi.api.player.filterNodesFromPosition(event/*, null, { nodes: idsToFilterBy }*/);
                //clear out our stored results
                this.currentFilterNodes = null;
                if (nodeIds && nodeIds.length) {
                    let nodeId = nodeIds[0];
                    if (db) {
                        // check if this node or any of it's parents have a click event
                        let parentId: string = nodeId;
                        while (parentId) {
                            if (db.byNode.hasOwnProperty(parentId)) {
                                let sceneUtils = this.sceneService.getSceneUtilities(this.sceneApi, eRuleType.scene, {}, null);
                                let clickArgs: IClickArgs = { clickedNode: sceneUtils.getNode({ id: parentId }), propogate: false };
                                db.byNode[parentId].forEach(h => h.handler(clickArgs));
                                if (!clickArgs.propogate) break;
                            }
                            parentId = this.sceneService.getParentNodeId(this.sceneApi, parentId);
                        }
                    }
                } else {
                    //empty space was clicked.  See if there was a handler put on the root scene node
                    let rootSceneId = this.sceneService.getRootSceneId(this.sceneApi);
                    if (db.byNode.hasOwnProperty(rootSceneId)) {
                        let sceneUtils = this.sceneService.getSceneUtilities(this.sceneApi, eRuleType.scene, {}, null);
                        let clickArgs: IClickArgs = { clickedNode: sceneUtils.getNode({ id: rootSceneId }), propogate: false };
                        db.byNode[rootSceneId].forEach(h => h.handler(clickArgs));
                    }
                }
            });
        }
    }

    public hover(event: clara2.IMouseEvent) {
        let clickdb = this.sceneApi.clickDb;
        let dragdb = this.sceneApi.draggableDb;
        if ((Object.keys(clickdb.byNode).length || Object.keys(dragdb.byNode).length) && !this.sceneApi.cameraAnimationInProgress && !this.sceneApi.activeDrag) {
            if (this.processingMouseOver) return;
            this.processingMouseOver = true;
            //let idsToFilterBy = Object.keys(db.nodeMap);
            let nodeIds = this.sceneApi.api.player.filterNodesFromPosition(event/*, null, { nodes: idsToFilterBy }*/);
            if (nodeIds && nodeIds.length) {
                let nodeId = nodeIds[0];

                // find the closest ancestor node with a click handler on it
                let hoverParentId = this.sceneService.findAncestorNodeId(
                    this.sceneApi,
                    nodeId,
                    nid => (clickdb.byNode.hasOwnProperty(nid) || dragdb.byNode.hasOwnProperty(nid))
                );
                if (hoverParentId) {
                    if (this.hoverParentId != hoverParentId) {
                        this.clearMouseOver();
                        this.hoverParentId = hoverParentId;

                        // now we have the parent that has the click handler.  
                        // The parent and all of it's children should have their materials changed
                        this.setMouseOverEffectRecursive(this.sceneApi, hoverParentId);
                        let handlers = clickdb.byNode[hoverParentId];
                        handlers && handlers.forEach(arg => { // if this is a hotspot, tell it it's hovered
                            if (arg.hotspot) arg.hotspot.$hover = true;
                        });
                    }
                } else {
                    this.clearMouseOver();
                }
            } else {
                this.clearMouseOver();
            }
            this.processingMouseOver = false;
        } else {
            this.clearMouseOver();
        }
    }

    
    public shimThree(sceneApi: ISceneApi) {
        let three = sceneApi.api.player.getThree().THREE;
        if (!three.$originalUpdateMatrixWorld) {
            let umw = three.$originalUpdateMatrixWorld = three.Object3D.prototype.updateMatrixWorld;
        }
        if (sceneApi.hierarchicalVisibility) {
            //THREE traverses from parent down updating the matrix world on every pass
            //But it doesn't pay attention to hierarchical visibility
            three.Object3D.prototype.updateMatrixWorld = function (force) {
                if (!this.visible) return;
                three.$originalUpdateMatrixWorld.apply(this, arguments);
            };
        } else {
            three.Object3D.prototype.updateMatrixWorld = three.$originalUpdateMatrixWorld;
        }

        /** override updateMatrix to add in a cache for the matrix vectors, so
         *  we're only rebuilding it when we have to.  Makes a big difference on render frame rates
         */
        three.Object3D.prototype.updateMatrix = function () {
            let createdCache = false;
            if (!this._positionCache) {
                this._positionCache = new three.Vector3();
                this._quaternionCache = new three.Quaternion();
                this._scaleCache = new three.Vector3(1, 1, 1);
                this._visibleCache = this.visible;
                createdCache = true;
            }
            if (createdCache ||
                (
                    !this._positionCache.equals(this.position)
                    || !this._quaternionCache.equals(this.quaternion)
                    || !this._scaleCache.equals(this.scale)
                    || this.visible != this._visibleCache
                )) {
                this.matrix.compose(this.position, this.quaternion, this.scale);
                this.matrixWorldNeedsUpdate = true;
                this._positionCache.copy(this.position);
                this._quaternionCache.copy(this.quaternion);
                this._scaleCache.copy(this.scale);
                this._visibleCache = this.visible;

            }
        }
    }

    public prefetchScenes(sceneApi: ISceneApi) {
        Object.keys(sceneApi.sceneDb).forEach(idScene => {
            if (!this.prefetchedScenes.hasOwnProperty(sceneApi.sceneDb[idScene].claraId)) {
                this.prefetchedScenes[sceneApi.sceneDb[idScene].claraId] = null;
                sceneApi.api.sceneIO.prefetch(
                    sceneApi.sceneDb[idScene].claraId,
                    sceneApi.sceneDb[idScene].publishHash,
                    { waitForPublish: true }
                );
            }
        });
    }

    public getRuleArgs(ruleType: eRuleType): ClaraSceneRuleArgs {
        let args = new ClaraSceneRuleArgs(this);
        args.configurator = this.sceneConfig.idProduct ? this.uiConfig : this.sceneConfig;
        args.kom = args.configurator.$manager;
        args.parentKom = args.configurator.$parentConfigurator ? args.configurator.$parentConfigurator.$manager : null;
        args.sceneKom = this.sceneConfig.$manager;
        args.sceneApi = this.sceneApi;
        args.isRender = this.isRender;
        args.sceneUtils = this.sceneService.getSceneUtilities(this.sceneApi, ruleType);
        args.clientLanguage = this.clientLanguage;

        return args;
    }
       
    protected resolveHotspotTargetNode(hotspot: Hotspot) {
        let idMap = hotspot.$parentConfigurator.$sceneIdMap;
        let targetNode = hotspot.targetNode;
        if (idMap) {
            targetNode = idMap[hotspot.targetNode] || hotspot.targetNode;
        }
        return targetNode;
    }

    protected customOverlayFn(div: HTMLDivElement) {
        let db = this.sceneApi.clickDb;
        let hotspotProcessed = false;
        for (let hotspot of this.$scope.allHotspots) {
            if (hotspot.target && hotspot.targetNode && hotspot.visible) {
                hotspotProcessed = true;
                let resolvedTargetNode = this.resolveHotspotTargetNode(hotspot);
                let pos = this.sceneApi.api.scene.getScreenPosition(resolvedTargetNode);
                if (pos) {
                    this.positionHotspot(pos, hotspot);
                }
                if (hotspot.targetShape == eHotspotShape.mesh && resolvedTargetNode) {
                    if (!db.byNode.hasOwnProperty(resolvedTargetNode) || !db.byNode[resolvedTargetNode].some(h => h.hotspot == hotspot)) {
                        this.sceneApi.addModelClickHandler({
                            modelId: resolvedTargetNode,
                            uniqueId: hotspot.$uid,
                            handler: () => this.hotspotClick({} as any, hotspot),
                            hotspot
                        });
                    }
                }
            }

            if (hotspot.targetShape == eHotspotShape.mesh && !hotspot.visible) {
                let resolvedTargetNode = this.resolveHotspotTargetNode(hotspot);
                // remove from click db if the hotspot is invisible
                if (resolvedTargetNode) {
                    if (db.byNode[resolvedTargetNode]) {
                        db.byNode[resolvedTargetNode].removeWhere(a => a.uniqueId == hotspot.$uid);
                        if (db.byNode[resolvedTargetNode].length == 0) {
                            delete db.byNode[resolvedTargetNode];
                        }
                    }
                }
            }
        }
        // call digest manually on this scope instead of scope.$apply because $apply starts 
        // at the root and digests everything.We only want to affect the hotspots here.
        if (hotspotProcessed) { //this can be expensive, so only run if there is something to update
            this.$scope.$digest();
        }
    }

    protected annotationFn(annotation: clara2.IAnnotation, div: HTMLDivElement) {
        // we don't call scope.$apply here because it's too slow, and the scope.$apply 
        // in the customOverlayFn takes care of making sure everything is updated anyways.
        // it's better in the customOverlay because that is only called once per global update, 
        // whereas this is called once for each annotation.

        // find the hotspot
        let hotspot: Hotspot = this.annotationToHotspotDb[annotation.id];
        // if it's an orphan annotation, then stop searching for it
        if (!hotspot && !this.annotationToHotspotDb.hasOwnProperty(annotation.id)) {
            hotspot = this.$scope.allHotspots.find(h => h.targetNode == annotation.id);
            this.annotationToHotspotDb[annotation.id] = hotspot;
        }

        if (hotspot) {
            hotspot.alpha = annotation.alpha;
            let pos = {
                x: annotation.left,
                y: annotation.top,
                top: annotation.top,
                left: annotation.left,
                width: 0,
                height: 0
            } as clara2.IScreenPosition;
            this.positionHotspot(pos, hotspot);
        }
    }

    private hotspotCounter: number = 0;
    protected hotspotClick($event: ng.IAngularEvent, hotspot: Hotspot) {
        hotspot.open = true;
        this.hotspotCounter++;
        hotspot.$zindex = this.hotspotCounter;

        if (hotspot.idCamera) {
            this.sceneService.animateCamera(this.sceneApi, {
                duration: hotspot.animationDuration,
                toCameraId: hotspot.idCamera,
                easing: hotspot.animationEasing
            });
        }
        let clickDb = this.sceneApi.clickDb;
        if (clickDb) {
            let nestedNullId = hotspot.$parentConfigurator.$uiConfigurator.$nestedSceneId;
            let handlerId = nestedNullId ? nestedNullId + "-" + hotspot.id : hotspot.id;
            if (clickDb.byNode.hasOwnProperty(handlerId)) {
                let clickArgs: IClickArgs = { propogate: false };
                clickDb.byNode[handlerId].forEach(h => h.handler(clickArgs));
            }
        }

        // some hotspot popups start off the screen because the height can't be calculated until it's shown
        // so we need to offload to recalculate the hotspot position after the digest
        this.$timeout(() => {
            let pos = this.sceneApi.api.scene.getScreenPosition(this.resolveHotspotTargetNode(hotspot));
            if (pos) {
                this.positionHotspot(pos, hotspot);
            }
        });
    }
    protected hotspotCloseClick($event: ng.IAngularEvent, hotspot: Hotspot) {
        $event.stopPropagation();
        hotspot.open = false;
    }

    protected clearAnnotations() {
        for (let h of this.$scope.allHotspots){
            if (h.target && h.allowClose) {
                h.open = false;
            }
        }
    }

    // protected toggleVrMode() {
    //     if (this.$scope.vrMode) {
    //         this.sceneApi.api.commands.deactivateCommand("vrMode");
    //     } else {
    //         this.sceneApi.api.commands.activateCommand("vrMode");
    //     }
    // }

    // protected toggleArMode() {
    //     if (this.$scope.vrMode) {
    //         this.sceneApi.api.commands.deactivateCommand("arMode");
    //     } else {
    //         this.sceneApi.api.commands.runCommand("arMode");
    //         // this.sceneApi.api.commands.activateCommand("arMode");
    //     }
    // }

    protected afterSetProperty(setArgs: ISceneSetPropertyArgs) {
        // if (setArgs.plug.isEqual("material") && setArgs.operator.isEqual("physical")) {
        //     let mouseOverMatId = this.hoverMaterialDb.get(setArgs.objectId);
        //     if (mouseOverMatId) {
        //         setArgs.objectId = mouseOverMatId;
        //         this.sceneService.setProperty(this.sceneApi, setArgs);
        //     }
        // }
    }

    protected afterAnimateProperty(animateArgs: ISceneAnimationArgs) {
        // if (animateArgs.plug.isEqual("material") && animateArgs.operator.isEqual("physical")) {
        //     let mouseOverMatId = this.hoverMaterialDb.get(animateArgs.objectId);
        //     if (mouseOverMatId) {
        //         animateArgs.objectId = mouseOverMatId;
        //         this.sceneService.animate(this.sceneApi, animateArgs);
        //     }
        // }
    }

    protected onSceneNested(nestedConfig: Configurator, nullNodeId: string) {
        // setup auto click to select page
        if (nestedConfig.$parentConfigurator.nestedSceneClickToSelect) {
            this.sceneApi.addModelClickHandler({
                modelId: nullNodeId,
                uniqueId: Utils.shortId(),
                handler: () => {
                    this.navigateToElement(nestedConfig);
                }
            });
        }
    }

    /** the id of the node that has a click handler that is causing the current hover to be active */
    private hoverParentId: string;
    private processingMouseOver: boolean = false;


    public clearHandlers() {
        this.sceneApi.draggableDb && this.sceneApi.draggableDb.clear();
        this.sceneApi.clickDb && this.sceneApi.clickDb.clear();
    }
    protected clearMouseOver() {
        // clear out old mouse over
        this.sceneService.clearHighlightsOfType(this.sceneApi, eHighlightType.hover);
        this.hovers.clear();
        this.hoverParentId = null;
        this.elem.style.cursor = "default";
        for (let h of this.$scope.allHotspots) { h.$hover = false };
    }

    // TODO:  remove this
    protected findAncestorNode(node: clara.INode, callback: (n: clara.INode) => boolean): clara.INode {
        let parent = node;
        while (parent) {
            if (callback(parent)) break;
            parent = parent.parentNode();
        }
        return parent;
    }


    protected setMouseOverEffectRecursive(sceneApi: ISceneApi, nodeId: string) {
        if (nodeId) {
            let api = sceneApi.api;
            let nodeType = api.scene.get({ id: nodeId, property: "type" });
            this.hovers.push({ nodeId });
            this.sceneService.highlight(sceneApi, { id: nodeId, on: true }, eHighlightType.hover);
            this.elem.style.cursor = "pointer";
        }
    }


    // public addModelClickHandler(args: IAddModelClickHandlerArgs) {
    //     let db = this.sceneApi.clickDb;
    //     // only add it to the list of handlers if the id doesn't already have a handler associated
    //     if (!db.byHandler.hasOwnProperty(args.uniqueId)) {
    //         db.byHandler[args.uniqueId] = args;
    //         // get all nodes that match the given query
    //         let query = this.sceneService.getQueryObject(this.sceneApi, args.modelId, null);
    //         let matchingNodes = this.sceneApi.cache.paths.getIds(query);

    //         matchingNodes.forEach(matchingNode => {
    //             if (!db.byNode.hasOwnProperty(matchingNode)) {
    //                 db.byNode[matchingNode] = [];
    //             }
    //             db.byNode[matchingNode].push(args);
    //         });
    //         //add the node id to map to itself
    //         // db.nodeMap[args.modelId] = args.modelId;
    //         //add children of the node to the map
    //         // this.sceneService.getChildNodeIds(this.sceneApi, args.modelId).forEach(i => db.nodeMap[i] = args.modelId);
    //     }
    // }

    // public addDraggable(d: IDraggable) {
    //     let sceneApi = this.sceneApi;
    //     d.$matchingNodes = {};
    //     let query = this.sceneService.getQueryObject(sceneApi, d.objectId, d.name);
    //     sceneApi.cache.paths.getIds(query).forEach(id => d.$matchingNodes[id] = {});
    // }

    public resize() {
        if (this.$scope.loaded) {
            this.sceneApi.api.player.resize();
        }
    }

    
    // public upload(): ng.IPromise<string> {
    //     // without streamchanges, the upload needs to do the following:
    //     // 1.  Upload the file to kbmax server
    //     // 2.  kbmax server imports the file to a public scene in clara that is strictly for uploads
    //     // 3.  now our client makes a call to importNode into this scene
    //     // 4.  in the callback of importNode, we can finally get back to the rule with the uploaded file
    //     return this.uploadService.promptUserToChooseFile().then(file => {
    //         // post the upload to kbmax
    //         let url = "api/scenes/upload";
    //         let postConfig: ng.IRequestShortcutConfig = {
    //             transformRequest: angular.identity, // add this to not serialize form data
    //             // set header to undefined so the browser sets the multipart content type
    //             headers: { "Content-Type": undefined }
    //         };
    //         let formData = new FormData();
    //         formData.append("file", file, file.name);

    //         let deferred = this.$q.defer<string>();
    //         // post the file
    //         this.$http.post<ISceneUploadResult>(url, formData, postConfig).then(response => {
    //             // returns the filename of the new node in the upload scene
    //             let uploadResult = response.data;

    //             // now we need to import the file node from the upload scene into this scene
    //             clara("importNode", {
    //                 el: this.elem,
    //                 importSceneId: uploadResult.uploadSceneId,
    //                 importSelector: uploadResult.filename,
    //                 parentId: "%MaterialLibrary",
    //                 callback: (err, uploadedNode) => {
    //                     if (err) {
    //                         deferred.reject(err);
    //                     } else {
    //                         deferred.resolve(uploadResult.filename);
    //                     }
    //                 }
    //             } as clara.IImportNodeCommandConfig);
    //         }, reason => {
    //             deferred.reject(reason);
    //         });

    //         // alert the user the file is uploading
    //         this.dialogService && this.dialogService.alert({
    //             msg: "Uploading file '" + file.name + "'",
    //             successMsg: "'" + file.name + "' uploaded successfully",
    //             promise: deferred.promise
    //         });

    //         return deferred.promise;
    //     });
    // }

    public runAction(actionName: string): ng.IPromise<IJsResult<ISceneRuleArgs>> {
        let args = this.getRuleArgs(eRuleType.action);
        return this.runActionRule(actionName, args);
    }

    public runFieldAction(elem: UiObject){
        let args = this.getRuleArgs(eRuleType.field);
        return this.ruleService.runRuleContainerAsync(elem, args).then(res => {
            this.handleRuleError(elem.name + " " + elem.$type, res);
            return res;
        });;
    }

    public runRules(waitForLoad = true): ng.IPromise<any> {
        let promise: ng.IPromise<any> = waitForLoad ? this.loadDeferred.promise : new KPromise(null) as any;

        return promise.then(() => {
            this.setFieldsIfStandalone();

            return this.sceneService.runRulesCycle(this.sceneConfig, this.uiConfig, this.getRuleArgs(eRuleType.scene))
                .then(result => {
                    this.sceneService.deleteOrphanedNestedScenes(this.sceneApi, this.uiConfig);
                    this.handleRuleError(eRuleType.scene, result);
                    //recalculate all hotspots
                    this.$scope.allHotspots = this.getAllHotspots();
                    
                    return result;
                });
        });
    }

    public importUploadFieldImage(uiConfig: Configurator, field: Field, file?: File): ng.IPromise<any> {
        return this.sceneService.importUploadFieldImage(this.sceneApi, uiConfig, field, file);
    }

    public snapshot(options: clara2.ISnapshotOptions) {
        return new KPromise(this.sceneService.snapshot(this.sceneApi, options)) as any;
    }

    public dispose() {
        if (this.elem){
            this.elem.remove();
            this.elem = null;
        }
    }
}
